class VariantAvailability extends HTMLElement {
  constructor() {
    super();
    this.form = this.closest('form[data-type="add-to-cart-form"]');
    if (!this.form) return;

    // Store references to containers
    this.backInStockContainer = this.querySelector('.back-in-stock-container');
    this.addToCartContainer = this.querySelector('.add-to-cart-container');
    this.soldOutContainer = this.querySelector('.sold-out-container');
    this.addToCartButton = this.addToCartContainer?.querySelector('button[type="submit"][name="add"]');

    // Get initial variant
    this.updateFromVariant(this.getSelectedVariant());

    // Listen for variant changes on the form
    this.boundOnFormChange = this.onFormChange.bind(this);
    this.form.addEventListener('change', this.boundOnFormChange);

    // Listen for variant change events from the pub/sub system
    if (window.theme?.pubsub) {
      this.variantChangeUnsubscriber = window.theme.pubsub.subscribe('variantChange', (event) => {
     
        if (event.data?.variant) {
          this.updateFromVariant(event.data.variant);
        }
      });
    }

    // Listen for direct variant change events
    this.onVariantChangedListener = (event) => {
      if (event.detail?.variant) {
        this.updateFromVariant(event.detail.variant);
      }
    };
    document.addEventListener('variant:change', this.onVariantChangedListener);

    // Listen for option value selection changes
    if (window.theme?.pubsub) {
      this.optionValueSelectionChangeUnsubscriber = window.theme.pubsub.subscribe('optionValueSelectionChange', (event) => {
     
        const variant = this.getSelectedVariant();
        if (variant) {
          this.updateFromVariant(variant);
        }
      });
    }
  }

  getSelectedVariant() {
    if (!this.form) return null;
    
    // Try to get variant from the form's variant JSON
    const variantJson = this.form.querySelector('[data-selected-variant]')?.textContent;
    if (variantJson) {
      try {
        const variant = JSON.parse(variantJson);
      
        return variant;
      } catch (e) {
        console.error('Error parsing variant JSON:', e);
      }
    }

    // Try to get variant from the variant picker
    const variantPicker = this.form.closest('variant-picker');
    if (variantPicker) {
      const selectedValues = variantPicker.selectedOptionValues;
      
    }

    return null;
  }

  onFormChange(event) {
  
    // Only handle variant changes
    if (!event.target.matches('input[name="id"]')) return;

    const variant = this.getSelectedVariant();
    if (variant) {
      this.updateFromVariant(variant);
    }
  }

  updateFromVariant(variant) {
    if (!variant) {
     
      return;
    }

    // Get variant status from metafields or data attribute
    let variantStatus = variant.metafields?.accentuate?.variant_status || null;
    
    // If not in variant object, check if we can get it from the element's data attribute
    if (!variantStatus) {
      variantStatus = this.dataset.variantStatus || null;
    }
    
    // Try to get it from the variant picker if available
    if (!variantStatus && this.form) {
      const variantPicker = this.form.closest('variant-picker');
      if (variantPicker) {
        const variantJson = variantPicker.querySelector('[data-selected-variant]')?.textContent;
        if (variantJson) {
          try {
            const variantData = JSON.parse(variantJson);
            variantStatus = variantData.metafields?.accentuate?.variant_status || null;
          } catch (e) {
            // Ignore parsing errors
          }
        }
      }
    }
    
    // Check if variant should show sold out button instead of back in stock
    const shouldShowSoldOut = variantStatus === 'dead-and-gone' || 
                              variantStatus === 'clearance' || 
                              variantStatus === 'discontinued';

    // Check if variant is sold out
    const isSoldOut = !variant.available || this.isQuantityRuleSoldout(variant);
  

    // Update the containers visibility based on variant status
    if (isSoldOut) {
      if (shouldShowSoldOut) {
        // Show sold out button, hide back in stock button
        this.showSoldOutButton();
        this.hideBackInStockButton();
      } else {
        // Show back in stock button, hide sold out button
        this.showBackInStockButton(variant);
        this.hideSoldOutButton();
      }
      this.hideAddToCartButton();
    } else {
      this.hideBackInStockButton();
      this.hideSoldOutButton();
      this.showAddToCartButton(variant);
    }

    // Update variant ID and title on back in stock button
    const backInStockButton = this.backInStockContainer?.querySelector('back-in-stock');
    if (backInStockButton) {
     
      backInStockButton.dataset.variantId = variant.id;
      backInStockButton.dataset.variantTitle = variant.title;
    }

    // Update hidden variant ID input
    const variantInput = this.form?.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = variant.id;
      variantInput.disabled = false;
    }
    
    // Update variant status on the element for future reference
    if (variantStatus) {
      this.dataset.variantStatus = variantStatus;
    }
  }

  isQuantityRuleSoldout(variant) {
    if (!variant) return false;
    
    if (variant.inventory_management === 'shopify' && variant.inventory_policy !== 'continue') {
      if (variant.quantity_rule && variant.quantity_rule.min > variant.inventory_quantity) {
        return true;
      }
    }
    return false;
  }

  showBackInStockButton(variant) {
    if (this.backInStockContainer) {
     
      this.backInStockContainer.style.display = '';

      // Update the back in stock button's variant ID and title
      const backInStockButton = this.backInStockContainer.querySelector('back-in-stock');
      if (backInStockButton) {
       
        backInStockButton.dataset.variantId = variant.id;
        backInStockButton.dataset.variantTitle = variant.title;
      }
    }
  }

  hideBackInStockButton() {
    if (this.backInStockContainer) {
    
      this.backInStockContainer.style.display = 'none';
    }
  }

  showSoldOutButton() {
    if (this.soldOutContainer) {
      this.soldOutContainer.style.display = '';
    }
  }

  hideSoldOutButton() {
    if (this.soldOutContainer) {
      this.soldOutContainer.style.display = 'none';
    }
  }

  showAddToCartButton(variant) {
    if (this.addToCartContainer) {
   
      this.addToCartContainer.style.display = '';
    }

    // Update button text
    if (this.addToCartButton) {
      const buttonTextSpan = this.addToCartButton.querySelector('.btn-text > span');
      if (buttonTextSpan) {
        const text = variant.template_suffix === 'pre-order' 
          ? window.theme?.strings?.preOrder || 'Pre-order'
          : window.theme?.strings?.addToCart || 'Add to cart';
        buttonTextSpan.textContent = text;
      }
    }
  }

  hideAddToCartButton() {
    if (this.addToCartContainer) {

      this.addToCartContainer.style.display = 'none';
    }
  }

  disconnectedCallback() {
    this.variantChangeUnsubscriber?.();
    this.optionValueSelectionChangeUnsubscriber?.();
    if (this.onVariantChangedListener) {
      document.removeEventListener('variant:change', this.onVariantChangedListener);
    }
    if (this.form && this.boundOnFormChange) {
      this.form.removeEventListener('change', this.boundOnFormChange);
    }
  }
}

customElements.define('variant-availability', VariantAvailability);